import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { CourseEquivalenceFormType } from '@/schemas/education/match-courses-schema'

const memoryStorage = () => {
  const store: Record<string, string> = {}

  return {
    getItem: async (name: string) => store[name] ?? null,
    setItem: async (name: string, value: string) => {
      store[name] = value
    },
    removeItem: async (name: string) => {
      delete store[name]
    }
  }
}

export type EquivalenceRecord = {
  requestId: string
  curriculumId: string
  courseEquivalences: CourseEquivalenceFormType
}

type EquivalenceStoreState = {
  records: EquivalenceRecord[]

  addRecord: (record: EquivalenceRecord) => void

  updateRecord: (
    requestId: string,
    curriculumId: string,
    courseEquivalences: CourseEquivalenceFormType
  ) => void

  removeRecord: (requestId: string, curriculumId: string) => void

  getRecord: (
    requestId: string,
    curriculumId: string
  ) => EquivalenceRecord | undefined

  clearAll: () => void
}

export const useEquivalenceStore = create<EquivalenceStoreState>()(
  persist(
    (set, get) => ({
      records: [],

      addRecord: (record) =>
        set({
          records: [...get().records, record]
        }),

      updateRecord: (requestId, curriculumId, courseEquivalences) =>
        set({
          records: get().records.map((r) =>
            r.requestId === requestId && r.curriculumId === curriculumId
              ? { ...r, courseEquivalences }
              : r
          )
        }),

      removeRecord: (requestId, curriculumId) =>
        set({
          records: get().records.filter(
            (r) =>
              !(r.requestId === requestId && r.curriculumId === curriculumId)
          )
        }),

      getRecord: (requestId, curriculumId) =>
        get().records.find(
          (r) => r.requestId === requestId && r.curriculumId === curriculumId
        ),

      clearAll: () => set({ records: [] })
    }),
    {
      name: 'equivalence-store',
      storage: createJSONStorage(memoryStorage)
    }
  )
)
